
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import BaseModal from '@/components/modals/BaseModal.vue';
import OfferReporterSuccessModal from '@/components/modals/offer-reporter/OfferReporterSuccessModal.vue';

import { ModalHelper } from '@/helpers/modal-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import Provider from '@/interfaces/provider';

import {
	Episode,
	Movie,
	MovieContent,
	ObjectType,
	Offer,
	Platform,
	Scoring,
	Season,
	SeasonContent,
	Show,
	ShowContent,
} from '@/@types/graphql-types';
import {
	ReportFeedbackDocument,
	ReportFeedbackMutation,
	ReportFeedbackMutationVariables,
} from '@/graphql/mutation/ReportFeedback.mutation';
import { SnowplowContext } from '@/helpers/tracking/providers';
import {
	GetSportProvidersByShortNamesDocument,
	GetSportProvidersByShortNamesQuery,
	GetSportProvidersByShortNamesQueryVariables,
} from '@/pages/graphql/queries/GetSportProvidersByShortNames.query';
import {
	GetTitleProvidersByShortNamesDocument,
	GetTitleProvidersByShortNamesQuery,
	GetTitleProvidersByShortNamesQueryVariables,
} from '@/pages/graphql/queries/GetTitleProvidersByShortNames.query';
import { APP_PLATFORM, APP_VERSION } from '@/stores/app.store';
import BasicButton from '@/ui-components/BasicButton.vue';

const Constant = namespace('constant');
const Language = namespace('language');
const User = namespace('user');

export type OfferReporterModalTitle = Pick<Movie | Show | Season | Episode, 'objectId' | 'objectType'> &
	Pick<MovieContent | ShowContent | SeasonContent, 'scoring'>;

@Component({
	name: 'OfferReporterModalGraphql',
	components: { BaseModal, BasicButton },
	apollo: {
		sportProvidersByShortName: {
			query: GetSportProvidersByShortNamesDocument,
			variables(): GetSportProvidersByShortNamesQueryVariables {
				return this.queryVariables;
			},
			update(data: GetSportProvidersByShortNamesQuery) {
				return data.packages.map(({ shortName }) => shortName);
			},
			skip() {
				return this.titleObjectType !== ObjectType.SportEvent;
			},
		},
		titleProvidersByShortName: {
			query: GetTitleProvidersByShortNamesDocument,
			variables(): GetTitleProvidersByShortNamesQueryVariables {
				return this.queryVariables;
			},
			update(data: GetTitleProvidersByShortNamesQuery) {
				return data.packages.map(({ shortName }) => shortName);
			},
			skip() {
				return this.titleObjectType === ObjectType.SportEvent;
			},
		},
	},
})
export default class OfferReporterModalGraphql extends Vue {
	sportProvidersByShortName: string[] = [];
	titleProvidersByShortName = [];

	@Prop({ required: true }) titleId: string;
	@Prop({ required: true }) titleObjectId: string;
	@Prop({ required: true }) titleObjectType:
		| ObjectType.Movie
		| ObjectType.ShowSeason
		| ObjectType.Show
		| ObjectType.ShowEpisode
		| ObjectType.SportEvent;
	@Prop({ required: true }) titleName: string;
	@Prop({ default: '' }) originalReleaseYear: number;
	@Prop({ default: () => {} }) titleScoring: Scoring;
	@Prop({ default: () => [] }) titleOffers: Offer[];
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];

	@Constant.State providers: Provider[];

	@Language.Getter language: string;
	@Language.Getter locale: string;
	@Language.Getter country: string;
	@Language.State webLocale: string;

	@User.Getter buyboxPresentationType: string;
	@User.State jwId: string;
	@User.State jwLoginId: string;

	commentInput = '';
	emailInput = '';
	errorNotification = false;
	externalLinkInput = '';
	isSending = false;
	issueSelected = '';
	providerSelected = '';
	providerSelectedClear = '';
	stayUpdated = false;

	get queryVariables(): GetSportProvidersByShortNamesQueryVariables {
		return {
			country: this.country,
			platform: Platform.Web,
		};
	}

	get issues() {
		const object_type = this.titleObjectTypeTranslation;
		const object_type_capitalized = this.capitalize(this.titleObjectTypeTranslation);

		// the keys are values expected from the backend
		// translation keys need to be present in whole for automatic cleaning
		return [
			{
				technical_name: 'mismatch',
				translatable: this.$t('WEBAPP_REPORT_ISSUE_MISMATCH', { object_type, object_type_capitalized }),
			},
			{
				technical_name: 'title_not_available',
				translatable: this.$t('WEBAPP_REPORT_ISSUE_MISSING_TITLE_NOT_AVAILABLE', {
					object_type,
					object_type_capitalized,
				}),
			},
			{
				technical_name: 'missing_offer',
				translatable: this.$t('WEBAPP_REPORT_ISSUE_MISSING_OFFER', { object_type, object_type_capitalized }),
			},
			{
				technical_name: 'wrong_price',
				translatable: this.$t('WEBAPP_REPORT_ISSUE_WRONG_PRICE', { object_type, object_type_capitalized }),
			},
			{
				technical_name: 'wrong_info',
				translatable: this.$t('WEBAPP_REPORT_ISSUE_METADATA', { object_type, object_type_capitalized }),
			},
			{
				technical_name: 'bug',
				translatable: this.$t('WEBAPP_REPORT_ISSUE_BUG', { object_type, object_type_capitalized }),
			},
			{
				technical_name: 'cant_watch',
				translatable: this.$t('WEBAPP_REPORT_ISSUE_CANT_WATCH', { object_type, object_type_capitalized }),
			},
			{
				technical_name: 'other',
				translatable: this.$t('WEBAPP_REPORT_ISSUE_OTHER', { object_type, object_type_capitalized }),
			},
		];
	}

	get issueName() {
		return {
			mismatch: 'MISMATCH',
			title_not_available: 'TITLE_NOT_AVAILABLE',
			missing_offer: 'MISSING_OFFER',
			wrong_price: 'WRONG_OFFER',
			wrong_info: 'WRONG_INFO',
			bug: 'BUG',
			other: 'OTHER',
		};
	}

	get freetextPlaceholder() {
		return this.mandatoryComment
			? this.$t('WEBAPP_REPORT_REQUIRED_FREETEXTPLACEHOLDER')
			: this.$t('WEBAPP_REPORT_FREETEXTPLACEHOLDER');
	}

	get mandatoryComment() {
		return ['wrong_info', 'bug', 'other'].includes(this.issueSelected);
	}

	get translatedCountry() {
		return this.$t(`WEBAPP_COUNTRY_${this.webLocale.toUpperCase()}`);
	}

	get titleObjectTypeTranslation() {
		const objectTypeTranslations = {
			[ObjectType.Movie]: this.$tc('WEBAPP_MOVIE'),
			[ObjectType.Show]: this.$tc('WEBAPP_SHOW'),
			[ObjectType.ShowSeason]: this.$tc('WEBAPP_SEASON'),
			[ObjectType.ShowEpisode]: this.$tc('WEBAPP_EPISODE'),
			[ObjectType.SportEvent]: this.$tc('WEBAPP_EVENT'),
		};

		return objectTypeTranslations[this.titleObjectType].toLowerCase();
	}

	get isFormInvalid(): boolean {
		if (this.mandatoryComment) {
			if (this.issueSelected && this.stayUpdated) {
				return this.emailInput !== '' && this.commentInput !== '' ? false : true;
			} else {
				return this.commentInput !== '' ? false : true;
			}
		} else {
			if (this.issueSelected && this.stayUpdated) {
				return this.emailInput !== '' && this.providerSelected !== '' ? false : true;
			} else {
				return this.providerSelected ? false : true;
			}
		}
	}

	get titleProviders() {
		const titleProviderIDs = this.titleOffers.map(offer => offer.package.packageId);
		return this.providers.filter(provider => titleProviderIDs.indexOf(provider.packageId) !== -1);
	}

	get filteredProviders() {
		const missingProvider = {
			clearName: `(${this.$t('WEBAPP_REPORT_MISSINGPROVIDER')})`,
			icon: '',
			monetizationTypes: [],
			packageId: 0,
			shortName: '',
			slug: '',
			technicalName: 'missing',
		} as Provider;
		const sportProviders = this.providers.filter(provider =>
			this.sportProvidersByShortName.includes(provider.shortName)
		);
		const titleProviders = this.providers.filter(provider =>
			// @ts-ignore
			this.titleProvidersByShortName.includes(provider.shortName)
		);
		const filtered = this.titleObjectType === ObjectType.SportEvent ? sportProviders : titleProviders;
		const providers = this.issueSelected === 'missing_offer' ? filtered.slice().sort() : this.titleProviders.sort();
		return [missingProvider].concat(providers);
	}

	get selectedIssueTranslatable() {
		return this.issues.find(el => el.technical_name === this.issueSelected)?.translatable || '...';
	}

	triggerPopoverOpen(e: Event) {
		e.stopPropagation();
		(e.target as HTMLElement).parentElement?.click();
	}

	async openPopover(event: Event) {
		const { popoverController } = await import('@/ionic.loader');

		const popover = await popoverController.create({
			cssClass: 'issues-popover',
			component: () => import('./IssuesListPopover.vue'),
			componentProps: { parent: this, propsData: { issues: this.issues, selectedIssue: this.issueSelected } },
			event,
		});

		await popover.present();

		popover.onWillDismiss().then(res => {
			const selectedIssue = res.data;
			selectedIssue && this.handleIssueChange({ target: { value: selectedIssue } });
		});
	}

	handleIssueChange(event: any) {
		this.commentInput = '';
		this.emailInput = '';
		this.errorNotification = false;
		this.externalLinkInput = '';
		this.isSending = false;
		this.providerSelected = '';
		this.providerSelectedClear = '';
		this.stayUpdated = false;
		this.issueSelected = event?.target?.value ?? '';
	}

	async openProviderPopover(event: Event) {
		const { popoverController } = await import('@/ionic.loader');

		const popover = await popoverController.create({
			cssClass: 'providers-popover',
			component: () => import('./ProviderListPopover.vue'),
			componentProps: {
				parent: this,
				propsData: { providers: this.filteredProviders, selectedProvider: this.providerSelectedClear },
			},
			event,
		});

		await popover.present();

		popover.onWillDismiss().then(res => {
			const selectedProvider = res.data;
			selectedProvider && this.handleProviderChange({ target: { value: selectedProvider } });
		});
	}

	handleProviderChange(event: any) {
		this.providerSelected = event?.target?.value.technicalName ?? '';
		this.providerSelectedClear = event?.target?.value.clearName ?? '';
	}

	async reportIssue() {
		this.isSending = true;

		const provider = this.providers.find((provider: Provider) => {
			return provider.technicalName === this.providerSelected;
		});

		try {
			if (this.emailInput) {
				const emailValid = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/.test(this.emailInput);
				if (!emailValid) {
					this.errorNotification = true;
					this.isSending = false;
					return;
				}
			}

			await this.$apollo.mutate<ReportFeedbackMutation, ReportFeedbackMutationVariables>({
				mutation: ReportFeedbackDocument,
				variables: {
					input: {
						id: this.titleId,
						// @ts-ignore
						type: this.issueName[this.issueSelected],
						country: this.country,
						language: this.language,
						package: provider?.shortName || '',
						comment: this.commentInput,
						externalUrl: this.externalLinkInput,
						email: this.emailInput,
						platformVersion: `${APP_VERSION}-${APP_PLATFORM}`,
					},
				},
			});

			TrackingHelper.trackEvent('offer_reporter', {
				action: 'send_feedback',
				label: this.issueSelected ? this.issueSelected : '',
			});

			// close current modal
			ModalHelper.close();
			// then open OfferReporterSuccessModal
			await ModalHelper.openModal(
				OfferReporterSuccessModal,
				{ email: this.emailInput },
				{},
				'',
				false,
				this.additionalContexts
			);
		} catch (error) {
			this.errorNotification = true;
		}

		this.isSending = false;
	}

	private capitalize(str: string) {
		return str.charAt(0).toUpperCase() + str.slice(1);
	}
}
